import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Row, Col, Avatar, Button, Form, Alert } from "antd";
import InputField from "../../../molecules/FormItems/InputField";
import FullPageLayout from "../../../templates/FullPageLayout";
import UploadPhoto from "../../../molecules/FormItems/UploadPhoto";
import InputPhone from "../../../molecules/FormItems/InputPhone";
import SelectField from "../../../molecules/FormItems/SelectField";
import TextArea from "../../../molecules/FormItems/TextArea";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getCities } from "../ducks/actions";
import RadioGroup from "../../../molecules/FormItems/RadioGroup";
import { addMember } from "../ducks/services";
import { AddMember } from "../ducks/types";
import BusinessBooks from "../Components/BusinessBooks";
import PersonalBooks from "../Components/PersonalBooks";
import { uploadUserImage } from "../../MyAccount/ducks/services";
import { AxiosError, AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";
import { CloseIcon } from "../../../atoms/CustomIcons";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";

export default function CreateMembers() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const citiesList = useAppSelector((state) => state?.members?.citiesList);
  const [loading, setLoading] = useState<boolean>(false);
  const [checklistFile, setChecklistFile] = useState<any>({});
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: any;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: any;
  }>({ file: "", imagePreviewUrl: null });
  const [citiesDropdown, setCitiesDropdown] = useState<
    { label: string; value: string }[]
  >([]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AddMember>({
    defaultValues: {
      fav_business_books: [
        {
          businessBook: "",
        },
      ],
      fav_personal_books: [
        {
          personalBooks: "",
        },
      ],
    },
  });

  const {
    fields: businessFields,
    append: businessAppend,
    remove: businessRemove,
  } = useFieldArray({
    control,
    name: "fav_business_books",
  });
  const {
    fields: personalFields,
    append: personalAppend,
    remove: personalRemove,
  } = useFieldArray({
    control,
    name: "fav_personal_books",
  });

  const fundingSource = watch("company_funding_source");

  useEffect(() => {
    dispatch(getCities());
  }, [dispatch]);

  useEffect(() => {
    if (citiesList?.length > 0) {
      let citiesArray: { label: string; value: string }[] = [];
      citiesList?.map((e: { name: string }) => {
        citiesArray.push({
          label: e?.name,
          value: e?.name,
        });
        return citiesArray;
      });
      setCitiesDropdown(citiesArray);
    }
  }, [citiesList]);

  const onSubmit = (val: AddMember) => {
    setLoading(true);
    let businessBooks: string[] = [];
    let personalBooks: string[] = [];
    val?.fav_business_books?.map((e: any) => {
      if (e?.businessBook) {
        return businessBooks.push(e?.businessBook);
      }
    });

    val?.fav_personal_books?.map((e: any) => {
      if (e?.personalBooks) {
        return personalBooks.push(e?.personalBooks);
      }
    });
    const payload = {
      fav_business_books: businessBooks,
      fav_personal_books: personalBooks,
      first_name: val?.first_name,
      last_name: val?.last_name,
      email: val?.email,
      position: val?.position,
      phone: val?.phone,
      short_location: val?.short_location,
      company_name: val?.company_name,
      company_website: val?.company_website,
      company_desc: val?.company_desc,
      company_headquarter_location:
        val?.company_headquarter_location ?? undefined,
      company_industry: val?.company_industry ?? undefined,
      company_funding_source: val?.company_funding_source,
      company_revenue: val?.company_revenue,
      company_amount_raised: val?.company_amount_raised ?? "$0",
    };
    addMember(payload)
      .then((res: AxiosResponse) => {
        if (checklistFile?.uid && res && res?.data?.id) {
          uploadUserImage({
            owner_id: res?.data?.id,
            image: checklistFile?.uid ? checklistFile : undefined,
            image_type: "user_image",
          })
            .then((res) => {
              if (res) {
                SuccessMessage("Member created.");
                setUploadModal(false);
                setLoading(false);
                navigate("/members");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          SuccessMessage("Member created.");
          navigate("/members");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const positionDropdown = [
    { label: "CEO", value: "CEO" },
    { label: "CTO", value: "CTO" },
    { label: "COO", value: "COO" },
    { label: "CFO", value: "CFO" },
    { label: "Founder", value: "Founder" },
    { label: "Co-Founder", value: "CoFounder" },
  ];

  const industryDropdown = [
    { label: "Aerospace & Defense", value: "aerospacedefense" },
    { label: "Agriculture/Livestock", value: "agriculturelivestock" },
    { label: "Automotive", value: "automotive" },
    { label: "Business Services (Printing, etc.)", value: "businessservices" },
    { label: "Construction", value: "construction" },
    { label: "Consumer Services", value: "consumerservices" },
    { label: "Education Training", value: "educationtraining" },
    { label: "Private equity", value: "privateequity" },
  ];

  const revenueDropdown = [
    { label: "$1 - 5M", value: "$1 - 5M" },
    { label: "$6 - 10M", value: "$6 - 10M" },
    { label: "$11 - 20M", value: "$11 - 20M" },
    { label: "$21 - 50M", value: "$21 - 50M" },
    { label: "$50M+", value: "$50M+" },
    { label: "Undisclosed", value: "Undisclosed" },
  ];

  const raisedDropdown = [
    { label: "$1 - 5M", value: "$1 - 5M" },
    { label: "$6 - 10M", value: "$6 - 10M" },
    { label: "$11 - 20M", value: "$11 - 20M" },
    { label: "$21 - 50M", value: "$21 - 50M" },
    { label: "$50M+", value: "$50M+" },
    { label: "Undisclosed", value: "Undisclosed" },
  ];

  const radioData: Array<{ label: string; value: string }> = [
    { label: "Bootstrapped", value: "bootstrapped" },
    { label: "VC-backed / Raised capital", value: "vc_backed" },
  ];

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  return (
    <FullPageLayout loading={loading}>
      <div className="invoice_head">
        <div className="left_head">
          <h3>Add member</h3>
        </div>
      </div>
      <div className="page_center member_form">
        <div className="center_all">
          <div className="center_head">
            <h3>Member profile details</h3>
            <p>Fill in the required information about the member.</p>
          </div>
          <div className="welcomeCard">
            <Alert
              icon={<Avatar src="/images/info-icon.svg" size={20} />}
              message="Welcome email"
              description="Member will receive a welcome email with the account credentials after the account is created."
              type="success"
              showIcon
              closable
            />
          </div>
          <Form
            layout="vertical"
            onFinish={handleSubmit(onSubmit)}
            className="members_form_area"
          >
            <Row gutter={24}>
              <Col span={24}>
                <h4>Personal details</h4>

                <UploadPhoto
                  uploadAvatar="/images/upload-avatar.svg"
                  width={88}
                  height={88}
                  saveUpload={saveUpload}
                  onImageDelete={() => {}}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldname="first_name"
                  label="First name"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter first name",
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldname="last_name"
                  label="Last name"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter last name",
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldname="email"
                  label="Email"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter email address",
                  }}
                  isRequired
                  rules={{
                    required: "Email is required.",
                    pattern: {
                      value: /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/,
                      message: "Email is required.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                />
              </Col>
              <Col span={12}>
                <SelectField
                  className="input_select"
                  fieldname="position"
                  label="Position"
                  control={control}
                  selectOption={positionDropdown}
                  isRequired={true}
                  rules={{ required: "Position is required." }}
                  iProps={{
                    placeholder: "Select position",
                  }}
                  validate={errors.position && "error"}
                  validMessage={errors.position && errors.position.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  className="input_phone"
                  fieldname="phone"
                  label="Phone number"
                  control={control}
                  // isRequired
                  // rules={{
                  //   required: "Error message",
                  // }}
                  // validate={errors.phone && "error"}
                  // validMessage={errors.phone && errors.phone.message}
                />
              </Col>
              <Col span={12}>
                <SelectField
                  className="input_select"
                  fieldname="short_location"
                  label="Location"
                  control={control}
                  selectOption={citiesDropdown}
                  isRequired={true}
                  rules={{ required: "Location is required." }}
                  iProps={{
                    placeholder: "Select city, country",
                  }}
                  validate={errors.short_location && "error"}
                  validMessage={
                    errors.short_location && errors.short_location.message
                  }
                />
              </Col>

              <div className="divider"> </div>

              <BusinessBooks
                control={control}
                append={businessAppend}
                remove={businessRemove}
                fields={businessFields}
                errors={errors}
              />

              <PersonalBooks
                control={control}
                append={personalAppend}
                remove={personalRemove}
                fields={personalFields}
                errors={errors}
              />

              <Col span={24}>
                <h4> Company details </h4>
              </Col>

              <Col span={12}>
                <InputField
                  fieldname="company_name"
                  label="Name"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter company name",
                  }}
                  isRequired
                  rules={{
                    required: "Company name is required.",
                  }}
                  validate={errors.company_name && "error"}
                  validMessage={
                    errors.company_name && errors.company_name.message
                  }
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldname="company_website"
                  label="Website"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter company website",
                  }}
                  isRequired
                  rules={{
                    required: "Company website is required.",
                  }}
                  validate={errors.company_website && "error"}
                  validMessage={
                    errors.company_website && errors.company_website.message
                  }
                />
              </Col>

              <Col span={24}>
                <TextArea
                  fieldname="company_desc"
                  label="Description"
                  control={control}
                  initValue=""
                  iProps={{
                    placeholder: "Enter company description",
                  }}
                />
              </Col>

              <Col span={12}>
                <SelectField
                  className="input_select"
                  fieldname="company_headquarter_location"
                  label="Headquarters location"
                  control={control}
                  selectOption={citiesDropdown}
                  iProps={{
                    placeholder: "Select city, country",
                  }}
                  // isRequired={true}
                  // rules={{ required: "Headquarters location is required" }}
                  // validate={errors.company_headquarter_location && "error"}
                  // validMessage={
                  //   errors.company_headquarter_location &&
                  //   errors.company_headquarter_location.message
                  // }
                />
              </Col>

              <Col span={12}>
                <SelectField
                  className="input_select"
                  fieldname="company_industry"
                  label="Industry"
                  control={control}
                  selectOption={industryDropdown}
                  iProps={{
                    placeholder: "Select Industry",
                    placement: "topLeft",
                  }}
                  // isRequired={true}
                  // rules={{ required: "Industry is required" }}
                  // validate={errors.company_industry && "error"}
                  // validMessage={
                  //   errors.company_industry && errors.company_industry.message
                  // }
                />
              </Col>

              <Col span={12}>
                <div className="fundingSource_radio">
                  <RadioGroup
                    fieldname="company_funding_source"
                    label="Funding source"
                    control={control}
                    optionsData={radioData}
                    initValue="bootstrapped"
                    onChangeRadio={() => {}}
                    isRequired={true}
                    rules={{ required: "Funding source is required." }}
                    validate={errors.company_funding_source && "error"}
                    validMessage={
                      errors.company_funding_source &&
                      errors.company_funding_source.message
                    }
                  />
                </div>
              </Col>

              <Col span={12}>
                <SelectField
                  className="input_select"
                  fieldname="company_revenue"
                  label="Revenue"
                  control={control}
                  selectOption={revenueDropdown}
                  isRequired={true}
                  rules={{ required: "Revenue is required." }}
                  iProps={{
                    placeholder: "Select revenue",
                    placement: "topLeft",
                  }}
                  validate={errors.company_revenue && "error"}
                  validMessage={
                    errors.company_revenue && errors.company_revenue.message
                  }
                />
              </Col>

              {fundingSource === "vc_backed" && (
                <Col span={12}>
                  <SelectField
                    className="input_select"
                    fieldname="company_amount_raised"
                    label="Amount raised"
                    control={control}
                    selectOption={raisedDropdown}
                    isRequired={true}
                    rules={{ required: "Amount raised is required." }}
                    iProps={{
                      placeholder: "Select Amount raised",
                    }}
                    validate={errors.company_amount_raised && "error"}
                    validMessage={
                      errors.company_amount_raised &&
                      errors.company_amount_raised.message
                    }
                  />
                </Col>
              )}
            </Row>

            <div className="discard_wrapper">
              <div className="discard_next_card">
                <div className="dn_left">
                  <Button
                    htmlType="button"
                    onClick={() => navigate("/members")}
                    type="text"
                    icon={<CloseIcon />}
                  >
                    Discard & Exit
                  </Button>
                </div>
                <div className="dn_right">
                  <Button htmlType="submit" className="icon_dark_btn">
                    Save member
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </FullPageLayout>
  );
}
